import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import CommentItem from './CommentItem';
import CommentComposer from './CommentComposer';
import { useCommentReplies } from '../../hooks/comments/useCommentReplies';
import {
  useCreateComment,
  useDeleteComment,
  useEditComment,
  useToggleReaction,
} from '../../hooks/comments/useCommentMutations';
import type { Comment, CommentTargetType, ReactionKind } from '../../types/comments';

interface CommentRepliesProps {
  parent: Comment;
  targetType: CommentTargetType;
  targetId: string;
  viewerId: string | null;
  viewerName: string | null;
  viewerAvatar: string | null;
  viewerRole: string | null;
  /** When true, the inline reply composer is rendered open. */
  composerOpen: boolean;
  onCloseComposer: () => void;
}

/**
 * Replies block under a top-level comment. Collapsed by default; expands
 * when the user has 1+ replies (auto-open) or clicks the "Show replies"
 * affordance. Lazy: the GET fires only once expanded.
 */
export default function CommentReplies({
  parent,
  targetType,
  targetId,
  viewerId,
  viewerName,
  viewerAvatar,
  viewerRole,
  composerOpen,
  onCloseComposer,
}: CommentRepliesProps) {
  const hasReplies = parent.reply_count > 0;
  const [expanded, setExpanded] = useState(hasReplies);
  const enabled = expanded && hasReplies;

  const { replies, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCommentReplies(parent.id, enabled);

  const createReply = useCreateComment();
  const editReply = useEditComment();
  const deleteReply = useDeleteComment();
  const toggleReaction = useToggleReaction();

  const handlePost = async (body: string) => {
    if (!viewerId || !viewerName) return;
    await createReply.mutateAsync({
      targetType,
      targetId,
      parentId: parent.id,
      body,
      author: {
        id: viewerId,
        name: viewerName,
        avatar_url: viewerAvatar,
        role: viewerRole,
      },
    });
    onCloseComposer();
  };

  const handleEdit = (reply: Comment) => (next: string) => {
    editReply.mutate({ comment: reply, body: next });
  };

  const handleDelete = (reply: Comment) => () => {
    if (confirm('Delete this reply? Other learners will see “[deleted]”.')) {
      deleteReply.mutate(reply);
    }
  };

  const handleReact = (reply: Comment) => (kind: ReactionKind, emoji?: string) => {
    toggleReaction.mutate({ comment: reply, kind, emoji });
  };

  return (
    <div className="space-y-3 pl-11">
      {hasReplies && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-500 hover:text-primary-600 dark:hover:text-primary-350 active:scale-[0.97] transition-transform duration-fast ease-out-strong"
        >
          <ChevronDownIcon className="w-3.5 h-3.5" />
          {parent.reply_count === 1 ? 'Show 1 reply' : `Show ${parent.reply_count} replies`}
        </button>
      )}

      {expanded && hasReplies && (
        <>
          {isLoading && replies.length === 0 && (
            <div className="text-xs text-text-tertiary">Loading replies…</div>
          )}
          {isError && (
            <div className="text-xs text-error-500">Couldn't load replies.</div>
          )}
          {replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              viewerId={viewerId}
              isTopLevel={false}
              onEdit={handleEdit(reply)}
              onDelete={handleDelete(reply)}
              onToggleReaction={handleReact(reply)}
            />
          ))}
          {hasNextPage && (
            <button
              type="button"
              onClick={fetchNextPage}
              disabled={isFetchingNextPage}
              className="text-xs font-semibold text-primary-500 hover:text-primary-600 dark:hover:text-primary-350 disabled:opacity-50"
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more replies'}
            </button>
          )}
          {replies.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-text-tertiary hover:text-text-secondary active:scale-[0.97] transition-transform duration-fast ease-out-strong"
            >
              <ChevronUpIcon className="w-3.5 h-3.5" />
              Hide replies
            </button>
          )}
        </>
      )}

      {composerOpen && (
        <div className="rounded-xl border border-border-primary/60 bg-surface-primary/50 p-3">
          <CommentComposer
            variant="reply"
            placeholder={`Reply to ${parent.author.name}…`}
            submitLabel="Reply"
            enabled={!!viewerId}
            onSubmit={handlePost}
            onCancel={onCloseComposer}
            isPending={createReply.isPending}
          />
        </div>
      )}
    </div>
  );
}
